import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminDashboard';
import { Mail, Phone, User, Clock, ChevronDown, ChevronUp, Inbox } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, { withCredentials: true })
      .then(r => setMessages(r.data.contacts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout active="/admin/messages">
      <div data-testid="admin-messages">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-dark-brown">Messages</h1>
            <p className="font-body text-sm text-light-brown mt-1">Contact form submissions from customers</p>
          </div>
          <div className="bg-rust/10 text-rust font-body text-sm font-semibold px-4 py-2 rounded-full">
            {messages.length} {messages.length === 1 ? 'message' : 'messages'}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner"></div></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-20">
            <Inbox className="w-16 h-16 text-border-custom mx-auto mb-4" />
            <p className="font-body text-lg text-light-brown">No messages yet</p>
            <p className="font-body text-sm text-light-brown/70 mt-1">Customer enquiries will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg, idx) => (
              <div key={msg.id || idx} className="bg-white rounded-xl border border-border-custom overflow-hidden" data-testid={`message-${idx}`}>
                {/* Header Row */}
                <button
                  className="w-full text-left p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-cream/30 transition-colors"
                  onClick={() => setExpanded(expanded === idx ? null : idx)}
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="shrink-0 w-10 h-10 rounded-full bg-rust/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-rust" />
                    </div>
                    <div>
                      <div className="font-body font-semibold text-dark-brown">{msg.name}</div>
                      <div className="flex flex-wrap items-center gap-3 mt-0.5">
                        <span className="flex items-center gap-1 font-body text-xs text-light-brown">
                          <Mail className="w-3 h-3" /> {msg.email}
                        </span>
                        {msg.phone && (
                          <span className="flex items-center gap-1 font-body text-xs text-light-brown">
                            <Phone className="w-3 h-3" /> {msg.phone}
                          </span>
                        )}
                      </div>
                      {/* Preview of message */}
                      {expanded !== idx && (
                        <p className="font-body text-sm text-mid-brown mt-1 line-clamp-1 max-w-md">{msg.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {msg.created_at && (
                      <span className="flex items-center gap-1 font-body text-xs text-light-brown">
                        <Clock className="w-3 h-3" />
                        {new Date(msg.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {expanded === idx
                      ? <ChevronUp className="w-4 h-4 text-light-brown" />
                      : <ChevronDown className="w-4 h-4 text-light-brown" />
                    }
                  </div>
                </button>

                {/* Expanded Message Body */}
                {expanded === idx && (
                  <div className="border-t border-border-custom bg-cream/40 px-5 py-4">
                    <div className="font-body text-xs font-semibold text-mid-brown uppercase tracking-wide mb-2">Message</div>
                    <p className="font-body text-sm text-dark-brown whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                    <div className="mt-4 flex gap-3 flex-wrap">
                      <a href={`mailto:${msg.email}`} className="inline-flex items-center gap-1.5 bg-rust text-cream font-body text-xs px-4 py-2 rounded-full hover:bg-rust/90 transition-colors">
                        <Mail className="w-3 h-3" /> Reply via Email
                      </a>
                      {msg.phone && (
                        <a href={`tel:${msg.phone}`} className="inline-flex items-center gap-1.5 bg-dark-brown text-cream font-body text-xs px-4 py-2 rounded-full hover:bg-dark-brown/90 transition-colors">
                          <Phone className="w-3 h-3" /> Call {msg.phone}
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
